import { recipeTotalMinutes } from "@batch-and-bowl/shared";
import {
  DIET_TAG_VALUES,
  PROTEIN_TAG_VALUES,
  RECOMPUTED_TAG_VALUES,
  canonicalizeTag,
  compareTagOrder,
} from "./recipe-tags";

/** Mirror of packages/database deriveDietaryTags' result. Passed in by the caller — validators must not depend on database. */
export interface DietDerivationInput {
  /** Canonical diet values proven by ingredients and missing from the input. */
  additions: readonly string[];
  /** Canonical diet values the ingredients refute. */
  disproven: readonly string[];
  /** Human-readable explanations, one per disproven value. */
  contradictions: readonly string[];
}

/** Mirror of packages/database deriveProteinTags' result (§4.1 addendum). Same shape/contract as DietDerivationInput. */
export interface ProteinDerivationInput {
  /** Canonical protein values proven by ingredients and missing from the input. */
  additions: readonly string[];
  /** Canonical protein values the ingredients refute. */
  disproven: readonly string[];
  /** Human-readable explanations, one per disproven value. */
  contradictions: readonly string[];
}

/** Mirror of packages/database deriveNutritionDiets' result (#829). Same shape/contract as DietDerivationInput. */
export interface NutritionDerivationInput {
  /** Canonical values ("low_carb", "keto") proven by nutrition and missing from the input. */
  additions: readonly string[];
  /** Canonical values the nutrition refutes. */
  disproven: readonly string[];
  /** Human-readable explanations, one per disproven value. */
  contradictions: readonly string[];
}

export interface FinalizeRecipeTagsInput {
  tags: readonly string[];
  prepTimeMinutes: number | null;
  cookTimeMinutes: number | null;
  totalTimeMinutes?: number | null;
  costEstimate: "budget" | "moderate" | "expensive" | null;
  dietDerivation?: DietDerivationInput;
  proteinDerivation?: ProteinDerivationInput;
  /** #829: applied identically to dietDerivation -- additions kept if in DIET_TAG_VALUES, disproven removed, contradictions surfaced. */
  nutritionDerivation?: NutritionDerivationInput;
}

export interface FinalizeRecipeTagsResult {
  /** Canonical, deduped, facet-sorted. */
  tags: string[];
  /** Inputs that are unknown or retired, in their original spelling. */
  dropped: string[];
  /** Contradiction messages for authored diet values the ingredients refute. */
  conflicts: string[];
}

const RECOMPUTED = new Set<string>(RECOMPUTED_TAG_VALUES);
const DIET = new Set<string>(DIET_TAG_VALUES);
const PROTEIN = new Set<string>(PROTEIN_TAG_VALUES);

/**
 * The only way a Recipe.tags array is produced (spec §5.2). Pure; never throws.
 * Callers decide what `dropped` / `conflicts` mean (seed: throw; API: 400; CLI: log/skip).
 */
export function finalizeRecipeTags(input: FinalizeRecipeTagsInput): FinalizeRecipeTagsResult {
  const kept = new Set<string>();
  const dropped: string[] = [];

  for (const raw of input.tags) {
    const value = canonicalizeTag(raw);
    if (value === null) {
      dropped.push(raw);
      continue;
    }
    if (RECOMPUTED.has(value)) continue; // always recomputed below
    kept.add(value);
  }

  const { cookTimeMinutes: cook } = input;
  const total = recipeTotalMinutes(input);
  if (total !== null && total <= 30) kept.add("quick");
  if (input.costEstimate === "budget") kept.add("budget");
  if (cook === 0) kept.add("no-cook");

  const conflicts: string[] = [];
  if (input.dietDerivation) {
    for (const added of input.dietDerivation.additions) {
      if (DIET.has(added)) kept.add(added);
    }
    for (const bad of input.dietDerivation.disproven) kept.delete(bad);
    conflicts.push(...input.dietDerivation.contradictions);
  }
  if (input.proteinDerivation) {
    for (const added of input.proteinDerivation.additions) {
      if (PROTEIN.has(added)) kept.add(added);
    }
    for (const bad of input.proteinDerivation.disproven) kept.delete(bad);
    conflicts.push(...input.proteinDerivation.contradictions);
  }
  if (input.nutritionDerivation) {
    for (const added of input.nutritionDerivation.additions) {
      if (DIET.has(added)) kept.add(added);
    }
    for (const bad of input.nutritionDerivation.disproven) kept.delete(bad);
    conflicts.push(...input.nutritionDerivation.contradictions);
  }

  return { tags: [...kept].sort(compareTagOrder), dropped, conflicts };
}
